import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  DataTable,
  Field,
  Moment,
  Resourceful,
  Select,
  StatusBadge,
  TextInput,
  Toolbar,
  type Column,
} from '../../shell/kit';
import { errorText } from '../../shell/errors';
import { useResource } from '../../shell/hooks';
import { useToast } from '../../shell/toast';
import type { CurrentUser, SiteSummary } from '../../api';
import { allowsAnywhere } from '../../api';
import type { ShellKey, Translate } from '../../i18n';
import {
  createEmployee,
  grantableRoles,
  listEmployees,
  type Employee,
  type EmployeeRole,
  type RoleName,
} from './api';
import type { StaffTranslate } from './i18n';

export interface ListTabProps {
  readonly t: StaffTranslate;
  readonly shellT: Translate;
  readonly user: CurrentUser;
  readonly siteId: string | null;
  readonly sites: readonly SiteSummary[];
  readonly onOpen: (employeeId: string) => void;
  readonly reloadKey: number;
}

const STATUS_TONE: Readonly<Record<string, 'ok' | 'warn' | 'neutral'>> = {
  active: 'ok',
  suspended: 'warn',
  dismissed: 'neutral',
};

export function ListTab(props: ListTabProps) {
  const toast = useToast();
  const [status, setStatus] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const canManage = allowsAnywhere(props.user, 'employee.manage');

  const people = useResource(
    () => listEmployees({ siteId: props.siteId, status, dismissed }),
    [props.siteId, status, dismissed, props.reloadKey],
  );

  // Какие роли можно выдать, решает сервер: копия правила в интерфейсе
  // разошлась бы с матрицей прав при первой же правке.
  const grantable = useResource(() => grantableRoles(), [props.reloadKey], {
    enabled: canManage,
  });

  const siteName = useMemo(() => {
    const map = new Map(props.sites.map((s) => [s.id, s.code]));
    return (id: string | null): string => (id ? (map.get(id) ?? '—') : props.t('staff.form.site.network'));
  }, [props.sites, props.t]);

  const columns: ReadonlyArray<Column<Employee>> = [
    {
      key: 'name',
      header: props.t('staff.col.name'),
      render: (row) => (
        <div className="min-w-0">
          <div className="font-medium text-ink">{row.displayName}</div>
          {row.phone ? <div className="text-xs text-ink-muted">{row.phone}</div> : null}
        </div>
      ),
    },
    {
      key: 'roles',
      header: props.t('staff.col.roles'),
      render: (row) =>
        row.roles.length === 0 ? (
          <span className="text-ink-muted">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.roles.map((r) => (
              <StatusBadge key={`${r.role}:${r.siteId ?? 'net'}`} tone="brand">
                {props.shellT(`role.${r.role}` as ShellKey)} · {siteName(r.siteId)}
              </StatusBadge>
            ))}
          </div>
        ),
    },
    {
      key: 'login',
      header: props.t('staff.col.login'),
      render: (row) =>
        row.login ? (
          <span className="font-mono text-xs">{row.login}</span>
        ) : (
          <span className="text-ink-muted">—</span>
        ),
    },
    {
      key: 'status',
      header: props.t('staff.col.status'),
      render: (row) => (
        <div className="flex flex-col gap-1">
          <StatusBadge tone={STATUS_TONE[row.status] ?? 'neutral'}>
            {props.t(`staff.status.${row.status}` as never)}
          </StatusBadge>
          {row.lockedUntil ? (
            <span className="text-xs text-warn">
              {props.t('staff.status.locked', { until: new Date(row.lockedUntil).toLocaleString('ru-RU') })}
            </span>
          ) : null}
          {row.mustChangePassword ? (
            <span className="text-xs text-ink-muted">{props.t('staff.mustChange')}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'totp',
      header: props.t('staff.col.totp'),
      render: (row) => (
        <span className={row.totpConfirmed ? 'text-ink' : 'text-ink-muted'}>
          {row.totpConfirmed ? props.t('staff.totp.on') : props.t('staff.totp.off')}
        </span>
      ),
    },
    {
      key: 'password',
      header: props.t('staff.col.password'),
      render: (row) => <Moment iso={row.passwordChangedAt} />,
    },
  ];

  return (
    <>
      <Toolbar>
        <Select
          aria-label={props.t('staff.filter.status')}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">{props.t('staff.filter.status.any')}</option>
          <option value="active">{props.t('staff.status.active')}</option>
          <option value="suspended">{props.t('staff.status.suspended')}</option>
          <option value="dismissed">{props.t('staff.status.dismissed')}</option>
        </Select>
        <Button onClick={() => setDismissed((v) => !v)} tone={dismissed ? 'primary' : 'ghost'}>
          {props.t('staff.filter.dismissed')}
        </Button>
        {canManage ? (
          <Button tone="primary" onClick={() => setCreating((v) => !v)}>
            {props.t('staff.add')}
          </Button>
        ) : null}
      </Toolbar>

      {creating && canManage ? (
        <CreateForm
          t={props.t}
          shellT={props.shellT}
          sites={props.sites}
          roles={grantable.data?.roles ?? []}
          network={grantable.data?.network ?? false}
          allowedSites={grantable.data?.sites ?? []}
          busy={busy}
          onCancel={() => setCreating(false)}
          onSubmit={async (input) => {
            setBusy(true);
            try {
              const created = await createEmployee(input);
              toast.ok(props.t('staff.form.created'));
              setCreating(false);
              people.reload();
              props.onOpen(created.id);
            } catch (err) {
              const shown = errorText(err);
              toast.error(shown.message, shown.code);
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}

      <Resourceful resource={people}>
        {(rows) => (
          <DataTable
            loading={people.loading}
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            onRowClick={(row) => props.onOpen(row.id)}
            empty={props.t('staff.empty')}
            emptyReason={props.t('staff.empty.reason')}
            noun="сотрудников"
          />
        )}
      </Resourceful>
    </>
  );
}

interface CreateFormProps {
  readonly t: StaffTranslate;
  readonly shellT: Translate;
  readonly sites: readonly SiteSummary[];
  readonly roles: readonly RoleName[];
  readonly network: boolean;
  readonly allowedSites: readonly string[];
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    displayName: string;
    phone?: string | null;
    login: string;
    password: string;
    pin?: string;
    roles: readonly EmployeeRole[];
  }) => void;
}

function CreateForm(props: CreateFormProps) {
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [role, setRole] = useState<RoleName | ''>(props.roles[0] ?? '');
  const [siteId, setSiteId] = useState<string>('');

  // Объекты, на которые актор вправе назначить. «Вся сеть» показывается только
  // тому, у кого сетевая привязка есть у самого.
  const sites = props.network
    ? props.sites
    : props.sites.filter((s) => props.allowedSites.includes(s.id));

  const ready = displayName.trim().length >= 2 && login.trim().length >= 3 && password.length > 0 && role;

  return (
    <Card title={props.t('staff.form.title')}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={props.t('staff.form.name')} required>
          <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field label={props.t('staff.form.phone')}>
          <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label={props.t('staff.form.login')} required>
          <TextInput
            value={login}
            autoComplete="off"
            onChange={(e) => setLogin(e.target.value.trim())}
          />
        </Field>
        <Field label={props.t('staff.form.password')} hint={props.t('staff.form.passwordHint')} required>
          <TextInput
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label={props.t('staff.form.pin')} hint={props.t('staff.form.pinHint')}>
          <TextInput inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={props.t('staff.form.role')} required>
            <Select value={role} onChange={(e) => setRole(e.target.value as RoleName)}>
              {props.roles.map((r) => (
                <option key={r} value={r}>
                  {props.shellT(`role.${r}` as ShellKey)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={props.t('staff.form.site')}>
            <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              {props.network ? <option value="">{props.t('staff.form.site.network')}</option> : null}
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <Button
          tone="primary"
          disabled={props.busy || !ready}
          onClick={() =>
            props.onSubmit({
              displayName: displayName.trim(),
              phone: phone.trim() || null,
              login: login.trim(),
              password,
              ...(pin.trim() ? { pin: pin.trim() } : {}),
              roles: [{ role: role as RoleName, siteId: siteId || null }],
            })
          }
        >
          {props.t('staff.form.submit')}
        </Button>
        <Button onClick={props.onCancel}>{props.t('staff.form.cancel')}</Button>
      </div>
    </Card>
  );
}
